/**
 * Die Suchmaske von ZIS Online.
 *
 * Ersetzt scripts/src/zisOnline.js der alten Seite (Typeahead für Hersteller
 * und Vertreiber, Blättern in diesen Listen, Sortierung) sowie den Teil von
 * multidigital.js, der die Filterkästen auf- und zuklappt, "alle"/"keine"
 * setzt und die Auswahl zurücksetzt. Beides lief auf jQuery, das der Relaunch
 * nicht mehr ausliefert.
 *
 * Zwei Fehler des Originals sind dabei nicht mitgewandert: das Blättern in der
 * Vertreiberliste rief die Herstellerliste auf, und Firmennamen wurden
 * ungeprüft als HTML eingesetzt.
 */

import { t } from './../common/localizer.ts';

/** Vom Server gelieferte Adresse einer Firma. */
interface ZisAddress {
    firma: string;
}

interface ZisAddressResult {
    totalRecords: number;
    addressesProducer: ZisAddress[] | null;
    addressesAgent: ZisAddress[] | null;
}

/** Ein Adressfilter: Suchfeld, Trefferliste, Blätterleiste. */
interface FilterConfig {
    /** Eingabefeld, in das der Benutzer tippt. */
    input: HTMLInputElement;
    /** Container für die Kontrollkästchen. */
    result: HTMLElement;
    /** Container für die Blätterleiste. */
    pagination: HTMLElement;
    /** Überschrift des Filterkastens - wird markiert, wenn etwas gewählt ist. */
    title: HTMLElement | null;
    /** Endpunkt, der die Firmen liefert. */
    url: string;
    /** Name des Formularfelds (producer oder distributor). */
    field: string;
    /** Bereits gewählte Firmen aus der Adresszeile. */
    selected: string[];
    /** Welche Liste der Antwort gilt. */
    kind: 'producer' | 'agent';
}

const PAGE_SIZE = 10;
const PAGES_AROUND = 3;
const MIN_CHARS = 3;

class ZisSearch {
    private readonly form: HTMLFormElement;

    constructor(form: HTMLFormElement) {
        this.form = form;

        this.setupFilterBoxes();
        this.setupSelectAll();
        this.setupReset();
        this.setupSorting();
        void this.setupAddressFilters();
    }

    // ------------------------------------------------------- Filterkästen

    /** Überschrift klappt den Filterkasten auf und zu. */
    private setupFilterBoxes(): void {
        for (const title of this.form.querySelectorAll<HTMLElement>('.filter .filter-titel')) {
            title.addEventListener('click', () => {
                const config = title.parentElement?.querySelector<HTMLElement>('.filter-config');

                if (config === null || config === undefined) {
                    return;
                }

                const open = config.style.display !== 'none';
                config.style.display = open ? 'none' : 'block';
                title.classList.toggle('active', !open);
            });
        }

        // Eine Produktgruppe klappt ihre Untereinträge auf.
        for (const button of this.form.querySelectorAll<HTMLElement>('.filter-button')) {
            button.addEventListener('click', (event) => {
                if ((event.target as HTMLElement).tagName === 'INPUT') {
                    return;
                }

                const parent = button.parentElement;

                if (parent === null) {
                    return;
                }

                const open = [...parent.querySelectorAll<HTMLElement>('.filter-list')]
                    .some((x) => x.style.display !== 'none');

                for (const list of parent.querySelectorAll<HTMLElement>('.filter-list')) {
                    list.style.display = open ? 'none' : 'block';
                }
            });
        }

        this.markActiveFilters();
    }

    /** "alle" und "keine" setzen alle Kästchen des Filters. */
    private setupSelectAll(): void {
        for (const box of this.form.querySelectorAll<HTMLInputElement>('.filter-config .alle, .filter-config .keine')) {
            box.addEventListener('change', () => {
                const config = box.closest('.filter-config');
                const check = box.classList.contains('alle') && box.checked;

                if (config === null) {
                    return;
                }

                // Erst den ganzen Kasten räumen - sonst bliebe eine zugeklappte
                // Untergruppe angehakt und filterte mit, ohne dass man es sieht.
                for (const item of this.filterBoxes(config)) {
                    item.checked = false;
                }

                if (check) {
                    for (const item of this.topLevelBoxes(config)) {
                        item.checked = true;
                    }
                }

                // Die Gegenseite wieder freigeben.
                const other = config.querySelector<HTMLInputElement>(
                    box.classList.contains('alle') ? '.keine' : '.alle'
                );

                if (other !== null) {
                    other.checked = false;
                }

                this.markActiveFilters();
            });
        }

        for (const box of this.form.querySelectorAll<HTMLInputElement>('.filter-config input[type=checkbox]')) {
            box.addEventListener('change', () => this.markActiveFilters());
        }
    }

    /** Alle Kästchen eines Filterkastens, ohne "alle" und "keine" selbst. */
    private filterBoxes(config: Element): HTMLInputElement[] {
        return [...config.querySelectorAll<HTMLInputElement>('input[type=checkbox]')].filter(
            (box) => !box.classList.contains('alle') && !box.classList.contains('keine')
        );
    }

    /**
     * Die oberste Ebene eines Filterkastens: die Hauptzeilen und die
     * nachgeladene Adressliste.
     *
     * "alle" hakt nur diese an. Die Untergruppen der Produktgruppe sind von
     * ihrer Hauptgruppe ohnehin abgedeckt; nähme man sie mit, baute
     * ZisQueryBuilder bei Gas rund 90 statt 9 Abfrageobjekte. Die alte Seite
     * machte es genauso.
     */
    private topLevelBoxes(config: Element): HTMLInputElement[] {
        return [
            ...config.querySelectorAll<HTMLInputElement>(
                ':scope > .form-check > input[type=checkbox]'
            ),
            ...config.querySelectorAll<HTMLInputElement>(
                '[data-zis-address-result] input[type=checkbox]'
            ),
        ].filter((box) => !box.classList.contains('alle') && !box.classList.contains('keine'));
    }

    /** Zurücksetzen leert alle Felder und schickt das Formular ab. */
    private setupReset(): void {
        const reset = this.form.querySelector<HTMLInputElement>('.btn-reset');

        reset?.addEventListener('click', (event) => {
            event.preventDefault();

            for (const box of this.form.querySelectorAll<HTMLInputElement>('input[type=checkbox]')) {
                box.checked = false;
            }

            for (const text of this.form.querySelectorAll<HTMLInputElement>('input[type=text]')) {
                text.value = '';
            }

            const sort = this.form.querySelector<HTMLInputElement>('#sort');

            if (sort !== null) {
                sort.value = '';
            }

            this.form.submit();
        });
    }

    /** Ein Filterkasten mit Auswahl wird hervorgehoben. */
    private markActiveFilters(): void {
        // Beides hängt am selben Auslöser - jede Änderung an einem Kästchen.
        this.refreshGroupStates();

        for (const section of this.form.querySelectorAll<HTMLElement>('.filter')) {
            const active = [...section.querySelectorAll<HTMLInputElement>('.filter-config input[type=checkbox]')]
                .some((x) => x.checked && !x.classList.contains('alle') && !x.classList.contains('keine'));

            section.querySelector('.filter-titel')?.classList.toggle('enabledfilter', active);
        }
    }

    /**
     * Eine Hauptgruppe zeigt einen Strich, sobald eine ihrer Untergruppen
     * gewählt ist - und keinen mehr, sobald die letzte wieder abgewählt wurde.
     *
     * Die alte Seite hatte das in multidigital.js, dort allerdings mit
     * el.parents().children(...): das lief über alle Vorfahren und setzte den
     * Strich bei jeder Hauptgruppe des Filters. Hier bekommt ihn nur die
     * Gruppe, zu der die Untergruppe gehört.
     */
    private refreshGroupStates(): void {
        for (const group of this.form.querySelectorAll<HTMLElement>('.filter-config > .form-check')) {
            const main = group.querySelector<HTMLInputElement>(':scope > input.main');

            if (main === null) {
                continue;
            }

            const children = group.querySelectorAll<HTMLInputElement>('.filter-list input.child');

            // Ist die Hauptgruppe selbst gewählt, deckt sie ihre Untergruppen
            // ohnehin ab - dann bleibt es beim Haken.
            main.indeterminate = !main.checked && [...children].some((x) => x.checked);
        }
    }

    // ---------------------------------------------------------- Sortierung

    /**
     * Ein Klick auf eine Spaltenüberschrift dreht die Richtung und schickt das
     * Formular ab - wie sortDatalist() der alten Seite.
     */
    private setupSorting(): void {
        const sortField = document.querySelector<HTMLInputElement>('#sort');

        for (const link of document.querySelectorAll<HTMLElement>('[data-zis-sort]')) {
            link.addEventListener('click', (event) => {
                event.preventDefault();

                if (sortField === null) {
                    return;
                }

                const field = link.dataset['zisSort'] ?? '';
                sortField.value = sortField.value === `${field}_up` ? `${field}_down` : `${field}_up`;
                this.form.submit();
            });
        }
    }

    // ----------------------------------------------- Hersteller/Vertreiber

    private async setupAddressFilters(): Promise<void> {
        const configs: FilterConfig[] = [];

        for (const element of this.form.querySelectorAll<HTMLElement>('[data-zis-address]')) {
            const input = element.querySelector<HTMLInputElement>('input[type=text]');
            const result = element.querySelector<HTMLElement>('[data-zis-address-result]');
            const pagination = element.querySelector<HTMLElement>('[data-zis-address-paging]');

            if (input === null || result === null || pagination === null) {
                continue;
            }

            const config: FilterConfig = {
                input,
                result,
                pagination,
                title: element.querySelector<HTMLElement>('.filter-titel'),
                url: element.dataset['zisAddress'] ?? '',
                field: element.dataset['zisAddressField'] ?? 'producer',
                selected: (element.dataset['zisAddressSelected'] ?? '')
                    .split(',')
                    .filter((x) => x !== ''),
                kind: element.dataset['zisAddressField'] === 'distributor' ? 'agent' : 'producer'
            };

            configs.push(config);

            input.addEventListener('input', () => {
                if (input.value.length >= MIN_CHARS || input.value.length === 0) {
                    void this.load(config, 1);
                }
            });
        }

        // Beim Laden die Listen füllen, wenn ein Suchtext in der Adresszeile steht.
        for (const config of configs) {
            if (config.input.value !== '') {
                await this.load(config, 1);
            }
        }
    }

    private async load(config: FilterConfig, page: number): Promise<void> {
        const url = `${config.url}?search=${encodeURIComponent(config.input.value)}&page=${page}&pageSize=${PAGE_SIZE}`;

        let data: ZisAddressResult;

        try {
            const response = await fetch(url, { headers: { Accept: 'application/json' } });

            if (!response.ok) {
                throw new Error(`Request failed: ${response.status}`);
            }

            data = (await response.json()) as ZisAddressResult;
        } catch {
            config.result.textContent = t('Zert_Es ist ein Fehler aufgetreten. Bitte versuchen Sie es erneut.');
            return;
        }

        const addresses = (config.kind === 'producer' ? data.addressesProducer : data.addressesAgent) ?? [];

        config.result.replaceChildren(...addresses.map((address) => this.renderOption(config, address)));
        this.renderPaging(config, page, Math.ceil(data.totalRecords / PAGE_SIZE));
        this.markActiveFilters();
    }

    /**
     * Ein Kontrollkästchen je Firma. Der Name wird über textContent gesetzt,
     * nicht über innerHTML - die alte Seite baute daraus einen HTML-String.
     */
    private renderOption(config: FilterConfig, address: ZisAddress): HTMLElement {
        const wrapper = document.createElement('div');
        wrapper.className = 'form-check';

        const input = document.createElement('input');
        input.type = 'checkbox';
        input.className = 'form-check-input';
        input.name = config.field;
        input.value = address.firma;
        input.id = `${config.field}-${address.firma.replace(/[^\w-]/g, '-')}`;
        input.checked = config.selected.includes(address.firma);
        input.addEventListener('change', () => this.markActiveFilters());

        const label = document.createElement('label');
        label.className = 'form-check-label small';
        label.htmlFor = input.id;
        label.textContent = address.firma;

        wrapper.append(input, label);

        return wrapper;
    }

    private renderPaging(config: FilterConfig, page: number, totalPages: number): void {
        config.pagination.replaceChildren();

        if (totalPages <= 1) {
            return;
        }

        const list = document.createElement('ul');
        list.className = 'pagination pagination-sm justify-content-end mb-0';

        const add = (label: string, target: number, active = false, disabled = false): void => {
            const item = document.createElement('li');
            item.className = `page-item${active ? ' active' : ''}${disabled ? ' disabled' : ''}`;

            const link = document.createElement('a');
            link.className = 'page-link';
            link.href = 'javascript:;';
            link.textContent = label;
            link.addEventListener('click', (event) => {
                event.preventDefault();

                if (!disabled && !active) {
                    void this.load(config, target);
                }
            });

            item.append(link);
            list.append(item);
        };

        add('«', 1, false, page <= 1);
        add('‹', page - 1, false, page <= 1);

        for (let number = page - PAGES_AROUND; number <= page + PAGES_AROUND; number++) {
            if (number > 0 && number <= totalPages) {
                add(number.toString(), number, number === page);
            }
        }

        add('›', page + 1, false, page >= totalPages);
        add('»', totalPages, false, page >= totalPages);

        config.pagination.append(list);
    }
}

const form = document.querySelector<HTMLFormElement>('#form__searchfilter');

if (form !== null) {
    // eslint-disable-next-line no-new
    new ZisSearch(form);
}
